<template>
  <div class="knowledge-graph-page">
    <div class="page-header">
      <div class="header-content">
        <button class="btn-back" @click="$router.back()">
          ← 返回
        </button>
        <h1>{{ pageTitle }}</h1>
      </div>
    </div>

    <div v-if="loading" class="loading-container">
      <div class="loading-spinner"></div>
      <p>正在加载知识图谱...</p>
    </div>

    <div v-else-if="errorMessage" class="error-container">
      <p>{{ errorMessage }}</p>
    </div>

    <div v-else class="graph-container">
      <main class="graph-main">
        <section class="graph-panel graph-panel--canvas">
          <div class="graph-panel__heading">
            <h2>知识关系图</h2>
          </div>
          <div v-show="graphNodes.length" ref="graphChart" class="graph-chart"></div>
          <div v-if="!graphNodes.length" class="empty-state">
            <p>暂无知识点数据</p>
          </div>
        </section>
      </main>
    </div>
  </div>
</template>

<script>
import echarts from 'echarts'
import { fetchCourseDetail } from '@/api/course'
import { fetchKnowledgeGraph } from '@/api/courseKnowledge'

export default {
  name: 'KnowledgeGraph',
  data() {
    return {
      loading: true,
      errorMessage: '',
      course: {},
      knowledgeGraph: {
        nodes: [],
        edges: []
      },
      chart: null
    }
  },
  computed: {
    courseId() {
      return Number(this.$route.params.courseId || this.$route.query.course_id || 0)
    },
    pageTitle() {
      return this.course.title ? `${this.course.title} - 知识图谱` : '知识图谱'
    },
    graphNodes() {
      return Array.isArray(this.knowledgeGraph.nodes) ? this.knowledgeGraph.nodes : []
    }
  },
  watch: {
    courseId(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.loadKnowledgeData()
      }
    }
  },
  created() {
    this.loadKnowledgeData()
  },
  mounted() {
    window.addEventListener('resize', this.resizeChart)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeChart)
    if (this.chart) this.chart.dispose()
  },
  methods: {
    async loadKnowledgeData() {
      if (!this.courseId) {
        this.errorMessage = '缺少课程ID'
        this.loading = false
        return
      }

      try {
        this.loading = true
        this.errorMessage = ''
        this.course = {}
        this.knowledgeGraph = { nodes: [], edges: [] }
        if (this.chart) {
          this.chart.dispose()
          this.chart = null
        }

        const [courseRes, graphRes] = await Promise.all([
          fetchCourseDetail(this.courseId).catch(() => ({})),
          fetchKnowledgeGraph(this.courseId)
        ])

        const graphData = graphRes || { nodes: [], edges: [] }

        this.course = courseRes || {}
        this.knowledgeGraph = {
          nodes: Array.isArray(graphData.nodes) ? graphData.nodes : [],
          edges: Array.isArray(graphData.edges) ? graphData.edges : []
        }
      } catch (error) {
        this.errorMessage = error.message || '加载知识图谱失败'
      } finally {
        this.loading = false
        await this.$nextTick()
        this.renderChart()
      }
    },

    renderChart() {
      if (!this.$refs.graphChart || !this.graphNodes.length) return
      if (!this.chart) {
        this.chart = echarts.init(this.$refs.graphChart)
      }

      const visibleNodes = this.graphNodes
      const visibleIds = new Set(visibleNodes.map(node => Number(node.id)))
      const chartNodes = visibleNodes.map(node => ({
        id: String(node.id),
        name: node.title || `知识点 #${node.id}`,
        level: Number(node.level) || 1,
        symbolSize: Math.max(28, 44 - (Number(node.level) || 1) * 3),
        itemStyle: {
          color: ['#6b1140', '#d51d73', '#5568d3', '#2a9d8f'][(Number(node.level) - 1) % 4],
          borderColor: '#fff',
          borderWidth: 2
        }
      }))
      const edges = visibleNodes
        .filter(node => visibleIds.has(Number(node.parent_id)))
        .map(node => ({ source: String(node.parent_id), target: String(node.id) }))

      this.chart.setOption({
        animationDuration: 350,
        tooltip: {
          trigger: 'item',
          formatter: params => params.dataType === 'node' ? `${params.name}<br>层级：${params.data.level}` : ''
        },
        series: [{
          type: 'graph',
          layout: 'force',
          roam: true,
          focusNodeAdjacency: true,
          data: chartNodes,
          edges,
          force: { repulsion: 220, edgeLength: [70, 150], gravity: 0.08 },
          label: { show: true, position: 'right', color: '#303133', fontSize: 12 },
          lineStyle: { color: '#c7cdd8', width: 1.5 },
          emphasis: { label: { show: true, fontWeight: 600 }, lineStyle: { width: 3 } }
        }]
      }, true)
    },

    resizeChart() {
      if (this.chart) this.chart.resize()
    }
  }
}
</script>

<style scoped>
.knowledge-graph-page {
  min-height: 100vh;
  background: #f5f5f5;
  display: flex;
  flex-direction: column;
}

.page-header {
  background: #fff;
  border-bottom: 1px solid #e0e0e0;
  padding: 20px 40px;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 20px;
}

.btn-back {
  padding: 8px 16px;
  border: 1px solid #d9d9d9;
  background: #fff;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.btn-back:hover {
  border-color: #1890ff;
  color: #1890ff;
}

.page-header h1 {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
}

.loading-container,
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 40px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #e0e0e0;
  border-top-color: #1890ff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.graph-container {
  display: flex;
  flex: 1;
  min-height: 0;
  padding: 24px;
  overflow: auto;
}

.graph-sidebar {
  width: 320px;
  background: #fff;
  border-right: 1px solid #e0e0e0;
  overflow-y: auto;
}

.sidebar-section {
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
}

.sidebar-section h3 {
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
}

.view-modes {
  display: flex;
  gap: 8px;
}

.mode-btn {
  flex: 1;
  padding: 10px;
  border: 1px solid #d9d9d9;
  background: #fff;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.mode-btn:hover {
  border-color: #1890ff;
  color: #1890ff;
}

.mode-btn.is-active {
  background: #1890ff;
  color: #fff;
  border-color: #1890ff;
}

.knowledge-tree {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.tree-search {
  width: 100%;
  box-sizing: border-box;
  margin-bottom: 12px;
  padding: 9px 11px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  color: #333;
  font: inherit;
}

.tree-search:focus {
  outline: none;
  border-color: #1890ff;
}

.tree-node {
  display: flex;
  flex-direction: column;
}

.node-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: #f5f5f5;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.node-item:hover {
  background: #e6f7ff;
}

.node-item.is-selected {
  background: #1890ff;
  color: #fff;
}

.node-icon {
  font-size: 12px;
}

.node-name {
  flex: 1;
}

.node-children {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 4px;
  padding-left: 20px;
}

.node-detail p {
  margin: 12px 0;
  font-size: 14px;
  line-height: 1.6;
}

.related-nodes {
  margin-top: 16px;
}

.related-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.related-tag {
  padding: 4px 12px;
  background: #e6f7ff;
  color: #1890ff;
  border-radius: 12px;
  font-size: 12px;
  transition: all 0.2s;
}

.related-tag:hover {
  background: #1890ff;
  color: #fff;
}

.graph-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.graph-overview {
  flex: 1;
  background: #fff;
  overflow-y: auto;
  padding: 32px;
}

.tree-view {
  flex: 1;
  overflow-y: auto;
  padding: 40px;
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #999;
  font-size: 16px;
}

.empty-state--panel {
  min-height: 160px;
  background: #f8fafc;
  border-radius: 12px;
}

.node-detail-card {
  max-width: 800px;
  margin: 0 auto;
  background: #fff;
  border-radius: 8px;
  padding: 40px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.node-detail-card h2 {
  margin: 0 0 24px 0;
  font-size: 28px;
  font-weight: 600;
}

.detail-section {
  margin-top: 32px;
}

.detail-section h4 {
  margin: 0 0 16px 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.detail-section p {
  margin: 0;
  font-size: 15px;
  line-height: 1.8;
  color: #666;
}

.detail-section p + p {
  margin-top: 8px;
}

.related-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
  margin-top: 16px;
}

.related-card {
  padding: 16px;
  background: #f5f5f5;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.related-card:hover {
  background: #e6f7ff;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.related-card h5 {
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.related-card p {
  margin: 0;
  font-size: 13px;
  color: #666;
  line-height: 1.6;
}

.graph-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.stat-card,
.graph-panel {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 6px 20px rgba(15, 23, 42, 0.04);
}

.stat-card {
  padding: 20px;
}

.stat-label {
  display: block;
  color: #64748b;
  font-size: 13px;
}

.stat-value {
  display: block;
  margin-top: 10px;
  color: #0f172a;
  font-size: 28px;
}

.graph-panel {
  padding: 24px;
}

.graph-panel--canvas {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: calc(100vh - 145px);
}

.graph-panel__heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.graph-panel__heading h2 {
  margin: 0;
  color: #0f172a;
  font-size: 20px;
}

.graph-chart {
  flex: 1;
  width: 100%;
  min-height: 560px;
}

@media (max-width: 720px) {
  .page-header {
    padding: 16px;
  }

  .graph-container {
    padding: 12px;
  }

  .graph-panel {
    padding: 16px;
  }

  .graph-panel--canvas {
    min-height: calc(100vh - 120px);
  }

  .graph-chart {
    min-height: 480px;
  }
}

.text-button {
  border: 0;
  padding: 0;
  background: transparent;
  color: #1890ff;
  cursor: pointer;
  font: inherit;
}

.text-button:hover {
  color: #096dd9;
}

.graph-panel + .graph-panel {
  margin-top: 20px;
}

.graph-panel h3 {
  margin: 0 0 16px 0;
  font-size: 18px;
  color: #0f172a;
}

.graph-node-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.graph-node-chip,
.relation-item {
  border: 1px solid #dbe3ef;
  background: #f8fafc;
  transition: all 0.2s;
}

.graph-node-chip {
  padding: 10px 14px;
  border-radius: 999px;
  cursor: pointer;
  color: #334155;
}

.graph-node-chip:hover,
.graph-node-chip.is-selected {
  background: #e0f2fe;
  border-color: #38bdf8;
  color: #0369a1;
}

.relation-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.relation-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 14px 16px;
  border-radius: 12px;
  cursor: pointer;
  text-align: left;
}

.relation-item:hover {
  background: #eef6ff;
  border-color: #93c5fd;
}

.relation-node {
  color: #0f172a;
  font-weight: 600;
}

.relation-arrow,
.relation-target {
  color: #64748b;
}
</style>
